import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client';
import { Cliente, CriarClienteRequest, AtualizarClienteRequest } from '../types';
import toast from 'react-hot-toast';
import { extractErrorMessage } from '../utils/errorHandler';

export const useClientes = () => {
  return useQuery({
    queryKey: ['clientes'],
    queryFn: async () => {
      const response = await api.get<{ clientes: Cliente[] }>('/clientes');
      return response.data.clientes;
    },
  });
};

export const useCliente = (id: number | null) => {
  return useQuery({
    queryKey: ['clientes', id],
    queryFn: async () => {
      const response = await api.get<Cliente>(`/clientes/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
};

export const useCriarCliente = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (cliente: CriarClienteRequest) => {
      const response = await api.post('/clientes', cliente);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
      toast.success('Cliente criado com sucesso!');
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error, 'Erro ao criar cliente'));
    },
  });
};

export const useAtualizarCliente = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...cliente }: AtualizarClienteRequest & { id: number }) => {
      const response = await api.put(`/clientes/${id}`, cliente);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
      toast.success('Cliente atualizado com sucesso!');
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error, 'Erro ao atualizar cliente'));
    },
  });
};

export const useRemoverCliente = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/clientes/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
      toast.success('Cliente removido com sucesso!');
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error, 'Erro ao remover cliente'));
    },
  });
};
